
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import DashboardOverview from './dashboard/DashboardOverview';
import ContentManagement from './content/ContentManagement';
import UserManagement from './users/UserManagement';
import AccountSettings from './settings/AccountSettings';
import ServiceManagement from './services/ServiceManagement';
import BrandKit from './content/BrandKit';
import MediaLibrary from './content/MediaLibrary';

const AdminDashboard = () => {
  const { hasPermission } = useAuth();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState(''); // Add search query state

  // Handle hash change to update active section
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      if (hash) {
        setActiveSection(hash);
      }
    };

    // Set initial section based on hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Render the active section
  const renderSection = () => {
    switch (activeSection) {
      case 'dashboard':
        return <DashboardOverview />;
      case 'content':
        return hasPermission('editor') ? <ContentManagement /> : <AccessDenied />;
      case 'media':
        return hasPermission('editor') ? <MediaLibrary searchQuery={searchQuery} /> : <AccessDenied />;
      case 'brand':
        return hasPermission('editor') ? <BrandKit /> : <AccessDenied />;
      case 'services':
        return hasPermission('editor') ? <ServiceManagement /> : <AccessDenied />;
      case 'users':
        return hasPermission('admin') ? <UserManagement /> : <AccessDenied />;
      case 'settings':
        return <AccountSettings />;
      default:
        return <DashboardOverview />;
    }
  };

  return (
    <div className="w-full">
      <header className="mb-6">
        <h1 className="text-3xl font-bold">{getTitle(activeSection)}</h1>
        <p className="text-muted-foreground">{getDescription(activeSection)}</p>
      </header>
      
      {renderSection()}
    </div>
  );
};

// Simple component for unauthorized access
const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center py-12">
    <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
    <p className="text-muted-foreground">You don't have permission to access this section.</p>
  </div>
);

// Helper function to get section title
const getTitle = (section: string): string => {
  switch (section) {
    case 'dashboard': return 'Dashboard';
    case 'content': return 'Content Management';
    case 'media': return 'Media Library';
    case 'brand': return 'Brand Kit';
    case 'services': return 'Service Management';
    case 'users': return 'User Management';
    case 'settings': return 'Account Settings';
    default: return 'Dashboard';
  }
};

// Helper function to get section description
const getDescription = (section: string): string => {
  switch (section) {
    case 'dashboard': return 'Overview of your site performance and activity';
    case 'content': return 'Manage website content, pages, and blog posts';
    case 'media': return 'Manage your media assets and files';
    case 'brand': return 'Access your brand assets and guidelines';
    case 'services': return 'Manage service offerings and related content';
    case 'users': return 'Manage users, roles, and permissions';
    case 'settings': return 'Configure your account settings';
    default: return 'Overview of your site performance and activity';
  }
};

export default AdminDashboard;
